import { useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { EventEntityType } from '@kfolks/types'
import { isAdmin } from '@/auth'
import { Badge } from '@/shared/components/atoms/ui/badge'
import { Button } from '@/shared/components/atoms/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/shared/components/atoms/ui/card'
import { Modal } from '@/components/Modal'
import { useAuth } from '@/hooks/useAuth'
import type { CommentApiClient, CommentDTO } from '@/features/request-center'
import { CommentComposer } from './CommentComposer'

/**
 * `<CommentsPanel>` — parent-agnostic comments surface (Spec 3 task 15.1).
 *
 * The component itself knows nothing about Request Center, IDM, or any
 * future consumer. It receives:
 *   - `entityType` / `entityId` — the parent the comments belong to
 *   - `apiClient` — a `CommentApiClient` that knows how to list / create /
 *     update / soft-delete against that parent's endpoint family
 *
 * Per ADR-0011, the apiClient call sites hit parent-scoped routes
 * (`/requests/:id/comments/*` for Request Center). New parent types ship
 * their own apiClient implementation; the panel stays the same.
 *
 * Visibility: the backend filters comment rows through
 * `buildCommentVisibilityPredicate` keyed on
 * `RequestApproverContext.viewerIsApprover`, so the rows we render here
 * are exactly what the viewer is authorised to see.
 */

export interface CommentsPanelProps {
	entityType: EventEntityType
	entityId: string
	apiClient: CommentApiClient
}

export function CommentsPanel({ entityType, entityId, apiClient }: CommentsPanelProps) {
	const queryClient = useQueryClient()
	const { user } = useAuth()
	const queryKey = ['comments', entityType, entityId] as const

	const { data, isLoading, isError } = useQuery({
		queryKey,
		queryFn: () => apiClient.list(entityId),
	})

	const createMutation = useMutation({
		mutationFn: (body: { content: string; isInternal?: boolean; mentions: string[] }) =>
			apiClient.create(entityId, {
				content: body.content,
				isInternal: body.isInternal,
				metadata: { mentions: body.mentions },
			}),
		onSuccess: () => queryClient.invalidateQueries({ queryKey }),
	})

	const updateMutation = useMutation({
		mutationFn: (vars: { commentId: string; content: string; mentions: string[] }) =>
			apiClient.update(entityId, vars.commentId, {
				content: vars.content,
				metadata: { mentions: vars.mentions },
			}),
		onSuccess: () => queryClient.invalidateQueries({ queryKey }),
	})

	const deleteMutation = useMutation({
		mutationFn: (commentId: string) => apiClient.softDelete(entityId, commentId),
		onSuccess: () => queryClient.invalidateQueries({ queryKey }),
	})

	const viewerIsAdmin = isAdmin(user?.role)
	const allComments = data?.data ?? []
	// Soft-deleted rows arrive with `deletedAt` set. Hide them entirely from
	// non-admins (the audit timeline shows the COMMENT_DELETED event); admins
	// see a placeholder so they know the row exists.
	const visibleComments = viewerIsAdmin ? allComments : allComments.filter((c) => !c.deletedAt)

	return (
		<Card>
			<CardHeader>
				<CardTitle className="text-base">Comments</CardTitle>
			</CardHeader>
			<CardContent className="space-y-4">
				{isLoading ? (
					<p className="text-sm text-muted-foreground">Loading…</p>
				) : isError ? (
					<p className="text-sm text-muted-foreground">Failed to load comments.</p>
				) : visibleComments.length === 0 ? (
					<p className="text-sm text-muted-foreground">No comments yet.</p>
				) : (
					<ol className="space-y-3" data-testid="comments-list">
						{visibleComments.map((comment) => (
							<CommentRow
								key={comment.id}
								comment={comment}
								canEdit={Boolean(user) && (viewerIsAdmin || comment.authorId === user?.userId)}
								canDelete={Boolean(user) && (viewerIsAdmin || comment.authorId === user?.userId)}
								onUpdate={(content, mentions) =>
									updateMutation.mutateAsync({ commentId: comment.id, content, mentions })
								}
								onDelete={() => deleteMutation.mutateAsync(comment.id)}
							/>
						))}
					</ol>
				)}

				<CommentComposer
					onSubmit={(content, mentions, isInternal) => createMutation.mutateAsync({ content, mentions, isInternal })}
					isSubmitting={createMutation.isPending}
					canPostInternal={viewerIsAdmin}
				/>
			</CardContent>
		</Card>
	)
}

// ─── CommentRow ──────────────────────────────────────────────────────────────

interface CommentRowProps {
	comment: CommentDTO
	canEdit: boolean
	canDelete: boolean
	onUpdate: (content: string, mentions: string[]) => Promise<unknown>
	onDelete: () => Promise<unknown>
}

function CommentRow({ comment, canEdit, canDelete, onUpdate, onDelete }: CommentRowProps) {
	const [editing, setEditing] = useState(false)
	const [confirmingDelete, setConfirmingDelete] = useState(false)
	const isDeleted = Boolean(comment.deletedAt)
	const when = new Date(comment.createdAt)
	const whenStr = Number.isNaN(when.getTime()) ? comment.createdAt : when.toLocaleString()
	const initialMentions = (comment.metadata as { mentions?: string[] }).mentions ?? []

	if (isDeleted) {
		// Admin-only branch (the panel filters this row out for non-admins).
		return (
			<li
				className="border-l-2 border-muted bg-muted/20 pl-3"
				data-testid="comment-row"
				data-deleted="true"
				data-comment-id={comment.id}
			>
				<p className="text-sm italic text-muted-foreground">[deleted]</p>
				<p className="text-xs text-muted-foreground">
					originally posted {whenStr} · soft-deleted at {comment.deletedAt}
				</p>
			</li>
		)
	}

	return (
		<li className="border-l-2 border-muted pl-3" data-testid="comment-row" data-comment-id={comment.id}>
			<div className="flex flex-wrap items-center gap-2 text-sm">
				<span className="font-medium" data-testid="comment-author">
					{comment.authorDisplayName ?? comment.authorId}
				</span>
				{comment.isInternal ? <Badge variant="secondary">internal</Badge> : null}
				<span className="text-xs text-muted-foreground">{whenStr}</span>
				{canEdit && !editing ? (
					<button
						type="button"
						onClick={() => setEditing(true)}
						className="ml-auto text-xs text-muted-foreground underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						Edit
					</button>
				) : null}
				{canDelete && !editing ? (
					<button
						type="button"
						onClick={() => setConfirmingDelete(true)}
						className="text-xs text-muted-foreground underline focus:outline-none focus-visible:ring-2 focus-visible:ring-ring"
					>
						Delete
					</button>
				) : null}
			</div>

			{editing ? (
				<CommentComposer
					initialContent={comment.content}
					initialMentions={initialMentions}
					submitLabel="Save"
					onSubmit={async (content, mentions) => {
						await onUpdate(content, mentions)
						setEditing(false)
					}}
					onCancel={() => setEditing(false)}
				/>
			) : (
				<p className="mt-1 whitespace-pre-wrap text-sm">{renderCommentBody(comment.content)}</p>
			)}

			<Modal open={confirmingDelete} onClose={() => setConfirmingDelete(false)} title="Delete comment?" maxWidth="sm">
				<p className="text-sm text-muted-foreground">
					This will soft-delete the comment. The audit timeline still records the deletion. Author + admin can trigger
					this.
				</p>
				<div className="mt-4 flex justify-end gap-2">
					<Button variant="outline" onClick={() => setConfirmingDelete(false)}>
						Cancel
					</Button>
					<Button
						variant="destructive"
						onClick={() => {
							void (async () => {
								await onDelete()
								setConfirmingDelete(false)
							})()
						}}
					>
						Delete
					</Button>
				</div>
			</Modal>
		</li>
	)
}

// ─── renderCommentBody ──────────────────────────────────────────────────────
//
// Splits a comment body into text + `@mention` tokens so mentions render
// underlined. The composer inserts `@<displayName>` followed by a space.
// Display names commonly contain one inner space (first + last), and the
// trailing word in a name almost always starts with an uppercase letter —
// arbitrary lowercase words after the mention are NOT part of the name.
//
// So the pattern matches `@` + first word, then optionally extends by
// `\s<UppercaseWord>` up to twice. That keeps "@Pat Contractor" as one
// chunk while leaving "please" or "review" outside the mention.
const MENTION_PATTERN = /@[\p{L}\p{N}._-]+(?:\s\p{Lu}[\p{L}\p{N}._-]*){0,2}/gu

function renderCommentBody(body: string): React.ReactNode {
	const out: React.ReactNode[] = []
	let lastIndex = 0
	let match: RegExpExecArray | null
	const re = new RegExp(MENTION_PATTERN.source, MENTION_PATTERN.flags)
	while ((match = re.exec(body)) !== null) {
		if (match.index > lastIndex) out.push(body.slice(lastIndex, match.index))
		out.push(
			<span key={`mention-${match.index}`} className="underline decoration-dotted underline-offset-2 font-medium">
				{match[0]}
			</span>,
		)
		lastIndex = match.index + match[0].length
	}
	if (lastIndex < body.length) out.push(body.slice(lastIndex))
	return out
}
